import { Popover } from "@shltelemedicine/shl-ui-lib";
import { ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { AddStudyFormInputs } from "../../../../models/study.interface";
import ConnectForm from "../../../Shared/ConnectForm/ConnectForm";
import EditableItemsList from "../../../Shared/EditableItemsList/EditableItemsList";
import EditableListItem from "../../../Shared/EditableItemsList/EditableListItem/EditableListItem";
import { PopoverContent } from "../AddStudyModal/AddStudyModal";
import styles from "./VisitsList.module.css";

const MAX_VISIT_ID_LENGTH = 12;

const initVisits = (
  initialVisits: { name: string; hasOrders?: boolean }[] = []
) =>
  Array.from(
    { length: 100 },
    (value, index) => initialVisits[index] ?? { name: undefined }
  );

const getLastIndexWithValue = (items: { name: string }[]) => {
  let lastIndexWithValue = -1;

  for (let i = 0; i < items?.length ?? 0; i++) {
    const item = items[i];
    if (item.name) {
      lastIndexWithValue = i;
    }
  }

  return lastIndexWithValue;
};

const VisitsList = () => {
  const [focusAddPlaceholder, setFocusAddPlaceholder] = useState(false);
  return (
    <ConnectForm<AddStudyFormInputs>>
      {({ setValue, watch }) => {
        const visits = initVisits(watch("visits") ?? []);

        const lastIndexWithValue = getLastIndexWithValue(visits);

        const visitsChangedHandler = (id: string, value: string) => {
          if (!visits) return;
          const index = +id;

          visits[index] = { name: value };

          setValue(
            "visits",
            visits.filter((v) => !!v.name),
            { shouldDirty: true }
          );
          setFocusAddPlaceholder(true);
        };

        const visitRemovedHandler = (id: string) => {
          const index = +id;
          visits.splice(index, 1);
          setValue(
            "visits",
            visits.filter((v) => !!v.name),
            { shouldDirty: true }
          );
          setFocusAddPlaceholder(false);
        };

        const isVisitValid = (
          id: string,
          value: string
        ): { isValid: boolean; errorMessage?: ReactNode } => {
          const isEmpty = !value;

          if (isEmpty) {
            return {
              isValid: false,
              errorMessage: (
                <FormattedMessage
                  id="MANAGEMENT.STUDIES.ADD_STUDY.VISIT_ID_REQUIRED"
                  defaultMessage="Insert visit ID"
                />
              ),
            };
          }

          const isItemExists = visits.filter(
            (visit, index) => +id !== index && visit.name === value
          ).length;

          if (isItemExists) {
            return {
              isValid: false,
              errorMessage: (
                <FormattedMessage
                  id="MANAGEMENT.STUDIES.ADD_STUDY.VISIT_ID_EXISTS"
                  defaultMessage="Visit ID already exists"
                />
              ),
            };
          }

          if (value.length > MAX_VISIT_ID_LENGTH) {
            return {
              isValid: false,
              errorMessage: (
                <FormattedMessage
                  id="MANAGEMENT.STUDIES.ADD_STUDY.VISIT_ID_MAX_LENGTH"
                  defaultMessage="Visit ID is too long"
                />
              ),
            };
          }

          return {
            isValid: true,
          };
        };

        return (
          <EditableItemsList
            title={
              <span className="capitalize">
                <FormattedMessage
                  id="MANAGEMENT.STUDIES.ADD_STUDY.VISIT_ID"
                  defaultMessage="VISIT ID"
                />
              </span>
            }
          >
            {visits?.map((visit, index) => {
              const disable = index > lastIndexWithValue;
              const showAddPlaceholder = index === lastIndexWithValue + 1;
              const focusVisit =
                index !== 0 && showAddPlaceholder && focusAddPlaceholder;

              return (
                <EditableListItem
                  key={index}
                  id={`${index}`}
                  count={index + 1}
                  initialValue={visit.name}
                  disableEdit={
                    (!showAddPlaceholder && disable) || visit.hasOrders
                  }
                  focusInput={focusVisit}
                  onChange={visitsChangedHandler}
                  onRemove={visitRemovedHandler}
                  validateInput={isVisitValid}
                >
                  {showAddPlaceholder ? (
                    <p
                      className={`mb-0 cursor-pointer ${styles.addItemPlaceholder}`}
                    >
                      <i>
                        <FormattedMessage
                          id="MANAGEMENT.STUDIES.ADD_STUDY.ADD_VISIT_PLACEHOLDER"
                          defaultMessage="Click to add visit name"
                        />
                      </i>
                    </p>
                  ) : visit.hasOrders ? (
                    <Popover
                      id={uuidv4()}
                      hoverContent={
                        <PopoverContent
                          messageContent={
                            <FormattedMessage
                              id="MANAGEMENT.STUDIES.EDIT_VIST_ID.POPUP_MESSAGE"
                              defaultMessage="This field cannot be edited.<br></br>The visit has orders assigned to it."
                              values={{
                                br: () => <br></br>,
                              }}
                            />
                          }
                        />
                      }
                    >
                      <span className="cursor-default">{visit.name}</span>
                    </Popover>
                  ) : (
                    <span className="cursor-pointer">{visit.name}</span>
                  )}
                </EditableListItem>
              );
            })}
          </EditableItemsList>
        );
      }}
    </ConnectForm>
  );
};

export default VisitsList;
